// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';

import { DashComponent, DashboardLauncher, DashboardReload } from 'wdna-components/lib/dashboards';
import { messageError } from 'wdna-components/lib/helpers/notification';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';

/* Types */
import { FilterContext } from '../../../../types';

/* Sidebar */
import Filter from './graphs/Filter';

/* Components */
const Container: React.LazyExoticComponent<any> = React.lazy(() => import('./graphs/Container'));

const DEFAULT_DASHBOARD_TIMEOUT: number = 24000000;
const dashConfig: DashComponent[] = [
  {
    component: Container,
    props: {
      title: '',
      container: 'PET',
      className: 'reading-container',
    },
  },
];

interface Props {
  rowId: number | undefined;
}

interface State {
  data1?: any;
  data2?: any;
  data3?: any;
  data4?: any;
  data5?: any;
  nameStation?: string;
  sidebar?: React.ReactNode[];
}

class Main extends React.Component<Props, State> {
  private pid: string;
  private mounted: boolean;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;

    this.state = {
      sidebar: undefined,
    };
  }

  public componentDidMount(): void {
    this.mounted = true;
    this.fetchData();
  }

  public componentDidUpdate(prevProps: Props): void {
    if (this.props !== prevProps) {
      this.fetchData();
    }
  }

  public componentWillUnmount(): void {
    this.mounted = false;
  }

  public setLocalState = (nextState: Partial<State>, callback?: (() => void)): void => {
    if (this.mounted) {
      this.setState((prevState: State) => ({
        ...prevState,
        ...nextState,
      }), callback);
    }
  };

  public updateParent = (data: State, cb?: (() => void)): void => {
    this.setLocalState({ ...data, }, () => {
      if (typeof cb === 'function') cb();
    });
  };

  public fetchData = (): void => {
    window.store.dispatch(initProcess(this.pid));
    try {
      this.processSidebarConfig();
    } catch (error: any) {
      messageError('Error', error.message);
    }

    setTimeout(() => {
      window.store.dispatch(finishProcess(this.pid));
    }, 1000);
  };

  public processSidebarConfig = (): void => {
    const sidebar: React.ReactNode[] = [(): JSX.Element => <Filter rowId={this.props.rowId} />];
    this.setLocalState({ sidebar });
  };

  public render(): React.ReactNode {
    const { sidebar } = this.state;
    if (!dashConfig || !sidebar) return null;
    return (
      <FilterContext.Provider value={{ parentState: this.state, updateParent: this.updateParent }}>
        <DashboardLauncher config={dashConfig} sidebar={sidebar} hiddenSidebar />
      </FilterContext.Provider>
    );
  }
}

const Dashboard: React.MemoExoticComponent<any> = React.memo((props: Props) => {
  return (
    <DashboardReload timeOut={DEFAULT_DASHBOARD_TIMEOUT}>
      <Main {...props} />
    </DashboardReload>
  );
});

export default Dashboard;
